import { Paintbrush, Blend, Signature } from "lucide-react";
import { OverviewSectionCard } from "@/components/agent/overview/OverviewSectionCard";
import { BrandingModal, type BrandingToolModal } from "@/components/branding";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Agent } from "@/types/agent/agent";
import { DEFAULT_BRANDING } from "@/types/ui/branding";
import { getGenieBrandTheme } from "@/utils/branding/genieBrandTheme";
import { useState } from "react";

interface BrandingCardProps {
  agent: Agent;
  brand?: string;
  onSwitchModal?: (modal: BrandingToolModal) => void;
}

export const BrandingCard = ({ agent, brand = "#4E9CFF", onSwitchModal }: BrandingCardProps) => {
  const [open, setOpen] = useState(false);
  const branding = agent.branding;
  const primaryColor = branding?.primaryColor || DEFAULT_BRANDING.primaryColor;
  const secondaryColor = branding?.secondaryColor;
  const logoUrl = branding?.logoUrl || branding?.customAvatarUrl;
  const gradientEnabled = branding?.gradientEnabled ?? false;
  // A genie with no saved signatureStyle has no signature anywhere (see BrandingModal's
  // normalizeBranding) — only show the badge once one has actually been saved and enabled.
  const signatureEnabled = Boolean(branding?.signatureStyle) && branding.signatureStyle!.enabled;
  const { primary: brandPrimary, withAlpha: tint } = getGenieBrandTheme(agent);
  // Deliberately not getGenieBrandTheme's `secondary` — that falls back to primary when
  // unset, which would make this badge match the gradient badge. This badge needs its own
  // distinct default so the two badges are always visually distinguishable.
  const brandSecondary = secondaryColor || "#6B7280";

  return (
    <>
      <OverviewSectionCard
        title="Branding"
        subtitle="Colors, logo and visual identity"
        genieIcon="palette"
        brand={brand}
        secondaryColor={branding?.secondaryColor}
        gradientEnabled={branding?.gradientEnabled}
        gradientAngle={branding?.gradientAngle}
      >
        <TooltipProvider>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4 flex-wrap">
            <Tooltip>
              <TooltipTrigger asChild>
                {logoUrl ? (
                  <img
                    src={logoUrl}
                    alt="Logo"
                    className="w-10 h-10 rounded-full object-cover border border-gray-200"
                  />
                ) : (
                  <div
                    className="w-10 h-10 rounded-full border border-gray-200"
                    style={
                      gradientEnabled && secondaryColor
                        ? { background: `linear-gradient(${branding?.gradientAngle ?? 135}deg, ${primaryColor}, ${secondaryColor})` }
                        : { backgroundColor: primaryColor }
                    }
                  />
                )}
              </TooltipTrigger>
              <TooltipContent>Brand logo</TooltipContent>
            </Tooltip>
            <div className="flex items-center gap-2 flex-wrap">
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-1.5">
                    <div className="w-4 h-4 rounded-full border border-gray-200" style={{ backgroundColor: primaryColor }} />
                    <span className="text-xs text-gray-500 font-mono">{primaryColor}</span>
                  </div>
                </TooltipTrigger>
                <TooltipContent>Primary color</TooltipContent>
              </Tooltip>
              {secondaryColor && (
                <>
                  <span className="text-gray-300">·</span>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex items-center gap-1.5">
                        <div className="w-4 h-4 rounded-full border border-gray-200" style={{ backgroundColor: secondaryColor }} />
                        <span className="text-xs text-gray-500 font-mono">{secondaryColor}</span>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>Secondary color</TooltipContent>
                  </Tooltip>
                </>
              )}
              {gradientEnabled && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge
                      variant="secondary"
                      className="gap-1 font-medium border-transparent"
                      style={{ backgroundColor: tint(brandPrimary, "1F"), color: brandPrimary }}
                    >
                      <Blend className="w-3 h-3" />
                      Gradient
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent>Gradient enabled</TooltipContent>
                </Tooltip>
              )}
              <Tooltip>
                <TooltipTrigger asChild>
                  {signatureEnabled ? (
                    <Badge
                      variant="secondary"
                      className="gap-1 font-medium border-transparent"
                      style={{ backgroundColor: tint(brandSecondary, "1F"), color: brandSecondary }}
                    >
                      <Signature className="w-3 h-3" />
                      Email Signature
                    </Badge>
                  ) : (
                    <Badge
                      variant="secondary"
                      className="gap-1 font-medium border-transparent"
                      style={{ backgroundColor: "#F3F4F6", color: "#6B7280" }}
                    >
                      <Signature className="w-3 h-3" />
                      No Email Signature
                    </Badge>
                  )}
                </TooltipTrigger>
                <TooltipContent>
                  {signatureEnabled ? "Email signature enabled" : "Custom email signature is not configured"}
                </TooltipContent>
              </Tooltip>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => setOpen(true)} className="shrink-0 gap-1.5">
            <Paintbrush className="w-3.5 h-3.5" />
            Edit
          </Button>
        </div>
        </TooltipProvider>
      </OverviewSectionCard>

      <BrandingModal
        key={`${agent.id}-${open}`}
        genie={agent}
        isOpen={open}
        onClose={() => setOpen(false)}
        onSwitchModal={
          onSwitchModal
            ? (modal) => {
                setOpen(false);
                onSwitchModal(modal);
              }
            : undefined
        }
      />
    </>
  );
};
